import { Menu } from "antd";
import moment from "moment";
import { useRef, useState } from "react";

import { useEndTime, useStartTime } from "../DateContext/DateContext";
import Chart from "./Chart";
import useNewMembers from "./useNewMembers";
import useTotalMembers from "./useTotalMembers";

export enum TimePeriod {
  Day = "day",
  Week = "week",
  Month = "month",
}

export enum MemberType {
  New = "new",
  Total = "total",
}

/**
 * MembersOverTimeCard props
 * teamId: Slack team id or Discord guild id
 * span: the number of columns we want the card to span in the page layout
 * expandMembersOverTime: wether the component is expanded or not
 * setExpandMembersOverTime: state function to expand the card in the parent
 */
interface Props {
  teamId: string;
  span: number;
  expandMembersOverTime: boolean;
  setExpandMembersOverTime: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function MembersOverTimeCard(props: Props) {
  const { teamId, span, expandMembersOverTime, setExpandMembersOverTime } =
    props;
  const { startTime, setStartTime } = useStartTime();
  const { endTime, setEndTime } = useEndTime();
  // eslint-disable-next-line react/no-unused-prop-types
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(TimePeriod.Day);
  const platform = localStorage.getItem("platform");
  const interval = useRef(TimePeriod.Day);
  const [memberType, setMemberType] = useState<MemberType>(MemberType.New);

  /**
   * This function makes sure the x axis shows the correct time intervals
   * @returns The interval to shown in the axis
   */
  const axisDisplay = () => {
    if (interval.current === TimePeriod.Day && timePeriod === TimePeriod.Week) {
      interval.current = TimePeriod.Week;
      return TimePeriod.Week;
    }
    if (
      interval.current === TimePeriod.Day &&
      timePeriod === TimePeriod.Month
    ) {
      interval.current = TimePeriod.Month;
      return TimePeriod.Month;
    }
    if (interval.current === TimePeriod.Week && timePeriod === TimePeriod.Day) {
      interval.current = TimePeriod.Day;
      return TimePeriod.Day;
    }
    if (
      interval.current === TimePeriod.Week &&
      timePeriod === TimePeriod.Month
    ) {
      interval.current = TimePeriod.Month;
      return TimePeriod.Month;
    }
    if (
      interval.current === TimePeriod.Month &&
      timePeriod === TimePeriod.Day
    ) {
      interval.current = TimePeriod.Day;
      return TimePeriod.Day;
    }
    if (
      interval.current === TimePeriod.Month &&
      timePeriod === TimePeriod.Week
    ) {
      interval.current = TimePeriod.Week;
      return TimePeriod.Week;
    }
    return interval.current;
  };

  const {
    totalMembersDay,
    totalMembersWeek,
    totalMembersMonth,
    loading: loadingTotal,
  } = useTotalMembers(startTime, endTime, teamId, platform as string);

  const {
    newMembersDay,
    newMembersWeek,
    newMembersMonth,
    loading: loadingNew,
  } = useNewMembers(startTime, endTime, teamId, platform as string);

  const start = moment(startTime);
  const end = moment(endTime);
  const numberOfWeeks = moment.duration(end.diff(start)).asWeeks();
  const numberOfMonths = moment.duration(end.diff(start)).asMonths();

  function handleMenuClick(e: any) {
    if (e.key === "1") {
      return;
    }
    if (e.key === "2") {
      setTimePeriod(TimePeriod.Day);
    }
    if (e.key === "3") {
      setTimePeriod(TimePeriod.Week);
    }
    if (e.key === "4") {
      setTimePeriod(TimePeriod.Month);
    }
  }

  function handleMemberType(e: any) {
    setMemberType(e.target.value);
  }

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="2">{TimePeriod.Day}</Menu.Item>
      {numberOfWeeks > 1 ? (
        <Menu.Item key="3">{TimePeriod.Week}</Menu.Item>
      ) : null}
      {numberOfMonths > 1 ? (
        <Menu.Item key="4">{TimePeriod.Month}</Menu.Item>
      ) : null}
    </Menu>
  );

  const popupContent = (text: string) => (
    <div>
      <p>{text}</p>
    </div>
  );

  return (
    <Chart
      platform={platform as string}
      axisDisplay={axisDisplay}
      totalMembersDay={totalMembersDay}
      totalMembersWeek={totalMembersWeek}
      totalMembersMonth={totalMembersMonth}
      newMembersDay={newMembersDay}
      newMembersWeek={newMembersWeek}
      newMembersMonth={newMembersMonth}
      handleMemberType={handleMemberType}
      menu={menu}
      popupContent={popupContent}
      expandMembersOverTime={expandMembersOverTime}
      setExpandMembersOverTime={setExpandMembersOverTime}
      timePeriod={timePeriod}
      memberType={memberType}
      loadingTotal={loadingTotal}
      loadingNew={loadingNew}
    />
  );
}
